import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import { AiAgentSession } from '@lgg/isomorphic/types/__generated__/graphql';
import { ContactLabel } from 'src/components/domain/contacts/contact-label';
import { ColoredTag, ColoredTagTheme } from 'src/components/general/display/colored-tag';
import { TableColumns, TableProps } from 'src/components/general/display/table';
import { Icon } from 'src/components/general/icon';
import { getDefaultSortOrder, TableSortData } from 'src/components/general/table-helpers';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import { ActivityIconWithDirection } from 'src/components/pages/activity/components/activity-icon-with-direction';
import {
  getRowOptionsColumn,
  KnowledgeSourceTable,
  TableLightText,
} from 'src/components/pages/ai-agents/components/ai-agents-table-columns';
import { useFormatDate } from 'src/hooks/use-format-date';
import { useUrls } from 'src/hooks/use-urls';

const SESSION_ICONS = { CHAT: 'aiSessionChat', VOICE: 'aiSessionVoice' } as const;

const CHIP_THEMES = {
  ACHIEVED: {
    color: 'secondaryMintDark',
    border: 'secondaryMint30',
    background: 'secondaryMint10',
  },
  PARTIALLY_ACHIEVED: {
    color: 'secondaryGoldDark',
    border: 'secondaryGold30',
    background: 'secondaryGold10',
  },
  NOT_ACHIEVED: { color: 'flint', border: 'koala', background: 'porcelain' },
  STATUS: { color: 'flint', border: 'koala', background: 'white' },
} satisfies Record<string, ColoredTagTheme>;

const SessionCell = styled(FlexRow)`
  align-items: center;

  > :first-child {
    margin: 0 15px 0 0;
  }
`;

const SessionTitle = styled.span`
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 13px;
  line-height: 16px;
`;

const Chips = styled(FlexRow)`
  gap: 4px;
  margin-top: 3px;
`;

const Chip = styled(ColoredTag)`
  align-items: center;
  border-radius: 9px;
  display: inline-flex;
  font-size: 11px;
  gap: 5px;
  height: 18px;
  letter-spacing: -0.22px;
  line-height: 13px;
  margin: 0;
  padding: 2px 5px 1px;
  text-transform: none;

  svg {
    height: 12px;
    width: 12px;
  }
`;

const ColumnText = styled(TableLightText)`
  white-space: nowrap;
`;

const StyledTable = styled(KnowledgeSourceTable)`
  ${up('md')} {
    && .ant-table-thead {
      height: 70px;
    }

    && .ant-table-tbody .ant-table-row .ant-table-cell:last-child {
      padding: 0 0 0 23px;
    }

    .ant-dropdown.context-menu-v2 {
      width: 192px;

      > div {
        padding: 4px 0;
      }
    }

    .adaptive-option-item {
      margin-left: 4px;
      margin-right: 4px;
      white-space: nowrap;
    }
  }
` as typeof KnowledgeSourceTable;

const SessionContactLabel = styled(ContactLabel)`
  .contact-label-text-column {
    flex: 1;
    min-width: 0;
    width: auto;

    > a,
    > span {
      width: 100%;
    }
  }
`;

const formatSessionDuration = (seconds: number | null | undefined): string => {
  if (seconds === null || seconds === undefined) {
    return '';
  }

  if (seconds < 60) {
    return `${seconds}s`;
  }

  const remainder = String(seconds % 60).padStart(2, '0');

  return `${Math.floor(seconds / 60)}:${remainder}m`;
};

const SessionDate = ({ date }: { date: string }) => {
  const { formatDate, formatSimpleRelativeDate } = useFormatDate();

  return (
    <ColumnText data-lgg-id="ai-agent-sessions-cell-date">
      {`${formatSimpleRelativeDate(date)}, ${formatDate(date, 'h:mm A')}`}
    </ColumnText>
  );
};

type SessionsTableProps = {
  data: AiAgentSession[];
  sortData: TableSortData;
  onChange: TableProps<AiAgentSession>['onChange'];
  onViewOverview: (session: AiAgentSession) => void;
};

export const SessionsTable = memo<SessionsTableProps>(
  ({ data, sortData, onChange, onViewOverview }) => {
    const { t } = useTranslation(['aiAgents']);
    const { getConversationUrl } = useUrls();

    const columns: TableColumns<AiAgentSession>[] = [
      {
        title: t('aiAgents:pages.agentDetails.sessions.columnHeaders.session'),
        key: 'session',
        render: (_, { type, status, goalStatus }) => (
          <SessionCell data-lgg-id="ai-agent-sessions-cell-session">
            <ActivityIconWithDirection icon={SESSION_ICONS[type]} direction="INBOUND" />
            <FlexColumn>
              <SessionTitle>
                {t(`aiAgents:pages.agentDetails.sessions.type.${type}`)}
              </SessionTitle>
              <Chips>
                {goalStatus && goalStatus !== 'NON_APPLICABLE' && (
                  <Chip
                    tagTheme={CHIP_THEMES[goalStatus]}
                    data-lgg-id="ai-agent-sessions-goal-chip"
                  >
                    {t(`aiAgents:pages.agentDetails.sessions.goalStatus.${goalStatus}`)}
                  </Chip>
                )}
                <Chip
                  tagTheme={CHIP_THEMES.STATUS}
                  data-lgg-id="ai-agent-sessions-status-chip"
                >
                  <Icon
                    type={
                      status === 'ACTIVE' ? 'aiSessionInProgress' : 'aiSessionFinished'
                    }
                  />
                  {t(`aiAgents:pages.agentDetails.sessions.status.${status}`)}
                </Chip>
              </Chips>
            </FlexColumn>
          </SessionCell>
        ),
      },
      {
        title: t('aiAgents:pages.agentDetails.sessions.columnHeaders.contact'),
        key: 'contact',
        width: '264px',
        render: (_, { contact }) =>
          contact ? (
            <SessionContactLabel
              contact={contact}
              marginSize={10}
              testId="ai-agent-sessions-cell-contact"
            />
          ) : null,
      },
      {
        title: t('aiAgents:pages.agentDetails.sessions.columnHeaders.duration'),
        key: 'duration',
        width: '135px',
        render: (_, { durationInSeconds }) => (
          <ColumnText data-lgg-id="ai-agent-sessions-cell-duration">
            {formatSessionDuration(durationInSeconds)}
          </ColumnText>
        ),
      },
      {
        title: t('aiAgents:pages.agentDetails.sessions.columnHeaders.date'),
        key: 'createdAt',
        width: '160px',
        showSorterTooltip: false,
        sorter: true,
        defaultSortOrder: getDefaultSortOrder(
          sortData.key === 'createdAt',
          sortData.direction,
        ),
        render: (_, { createdAt }) => <SessionDate date={createdAt} />,
      },
      getRowOptionsColumn<AiAgentSession>((session) => [
        {
          label: t('aiAgents:pages.agentDetails.sessions.options.viewOverview'),
          icon: 'summary',
          'data-lgg-id': 'ai-agent-sessions-option-view-overview',
          onClick: () => onViewOverview(session),
        },
        ...(session.contact
          ? [
              {
                label: t('aiAgents:pages.agentDetails.sessions.options.viewConversation'),
                icon: 'conversation',
                'data-lgg-id': 'ai-agent-sessions-option-view-conversation',
                to: getConversationUrl(session.contact.lastConversation.id),
              },
            ]
          : []),
      ]),
    ];

    return (
      <StyledTable
        rowKey={({ id }) => id}
        data-lgg-id="ai-agent-sessions-table"
        columns={columns}
        tableLayout="fixed"
        loading={false}
        dataSource={data}
        pagination={false}
        showHeader={true}
        scroll={{ x: 1069 }}
        onChange={onChange}
      />
    );
  },
);
